/**
 * RewardsSection — Points Progression & Rewards
 *
 * Displays user's points progression, level, and unlockable rewards.
 * Fully data-driven: hero image, how-to-play steps, rewards carousel,
 * and requirement units are all configurable via props.
 *
 * Falls back to Monster Energy defaults when no config is provided
 * (backward compatible with hardcoded HubPage.tsx usage).
 */

import React, { useRef, useState } from "react";
import { FaLock, FaUnlock, FaChevronLeft, FaChevronRight, FaTrophy, FaShoppingCart, FaGamepad, FaGift, FaStar } from "react-icons/fa";
import { MdLeaderboard } from "react-icons/md";
import "./RewardsSection.scss";

interface RewardTier {
    id: string;
    name: string;
    description?: string;
    imageUrl?: string;
    requiredLevel: number;
    requiredPoints?: number;
    bonusText?: string;
    category: "IN_GAME" | "MERCH" | "EXCLUSIVE" | "XP_BOOST";
}

interface HowToPlayStep {
    title: string;
    description: string;
    icon?: string;
}

interface RewardsSectionProps {
    brandName?: string;
    pointsName?: string;
    currentPoints?: number;
    currentLevel?: number;
    nextLevelPoints?: number;
    rewards?: RewardTier[];
    heroImageUrl?: string;
    /** Custom product spread image URL — replaces Monster Energy cans when no heroImageUrl */
    spreadImageUrl?: string;
    /** Controls the product spread visual: "none" hides it, "custom" uses spreadImageUrl, default shows Monster cans */
    spreadMode?: "none" | "cans" | "custom";
    howToPlaySteps?: HowToPlayStep[];
    requirementUnit?: string;
    onEnterCode?: () => void;
    onViewMissions?: () => void;
    onViewChallenges?: () => void;
    onViewRetailers?: () => void;
    themeColors?: {
        primary?: string;
        secondary?: string;
        accent?: string;
    };
}

// CDN base URL for Monster Energy assets (backward compat fallback)
const MONSTER_CDN_BASE = "https://storage.googleapis.com/cdn_devourgo/images/monster-energy";

// Default demo rewards based on Monster Energy CoD pattern
const DEFAULT_REWARDS: RewardTier[] = [
    {
        id: "reward-1",
        name: "Large Decal - Energy Flash",
        description: "Vehicle decal",
        imageUrl: `${MONSTER_CDN_BASE}/reward-01.webp`,
        requiredLevel: 1,
        requiredPoints: 100,
        bonusText: "+ 15 min 2XP",
        category: "IN_GAME",
    },
    {
        id: "reward-2",
        name: "Weapon Blueprint - Peacekeeper MK1 Hyper Green",
        description: "Weapon Blueprint",
        imageUrl: `${MONSTER_CDN_BASE}/reward-02.webp`,
        requiredLevel: 2,
        requiredPoints: 250,
        bonusText: "+ 15 min 2XP",
        category: "IN_GAME",
    },
    {
        id: "reward-3",
        name: "Operator Skin - Green Fury",
        description: "Exclusive Operator Skin",
        imageUrl: `${MONSTER_CDN_BASE}/reward-03.webp`,
        requiredLevel: 3,
        requiredPoints: 500,
        bonusText: "+ 15 min 2XP",
        category: "IN_GAME",
    },
    {
        id: "reward-4",
        name: "Weapon Blueprint - VS Recon Green Thunder",
        description: "Weapon Blueprint",
        imageUrl: `${MONSTER_CDN_BASE}/reward-04.webp`,
        requiredLevel: 4,
        requiredPoints: 750,
        bonusText: "+ 15 min 2XP",
        category: "IN_GAME",
    },
    {
        id: "reward-5",
        name: "Operator Skin - Daylight Ripper",
        description: "Exclusive Operator Skin",
        imageUrl: `${MONSTER_CDN_BASE}/reward-05.webp`,
        requiredLevel: 5,
        requiredPoints: 1000,
        bonusText: "+ 15 min 2XP",
        category: "IN_GAME",
    },
    {
        id: "reward-6",
        name: "15 min 2XP",
        description: "Double XP Boost",
        imageUrl: `${MONSTER_CDN_BASE}/reward-06.webp`,
        requiredLevel: 6,
        requiredPoints: 1500,
        category: "XP_BOOST",
    },
];

const DEFAULT_HOW_TO_PLAY: HowToPlayStep[] = [
    { title: "BUY", description: "Grab Monster Energy Gaming products at participating retailers", icon: "shopping-cart" },
    { title: "PLAY", description: "Dominate in Call of Duty and complete challenges", icon: "gamepad" },
    { title: "REDEEM", description: "Enter your code and unlock exclusive in-game rewards", icon: "trophy" },
];

const STEP_ICON_MAP: Record<string, React.ReactNode> = {
    "shopping-cart": <FaShoppingCart />,
    "gamepad": <FaGamepad />,
    "trophy": <FaTrophy />,
    "gift": <FaGift />,
    "star": <FaStar />,
    "leaderboard": <MdLeaderboard />,
};

const getStepIcon = (iconName?: string): React.ReactNode => {
    if (!iconName) return <FaStar />;
    return STEP_ICON_MAP[iconName] || <FaStar />;
};

const getCategoryIcon = (category: RewardTier["category"]) => {
    switch (category) {
        case "IN_GAME":
            return <FaGamepad />;
        case "MERCH":
            return <FaGift />;
        case "EXCLUSIVE":
            return <FaTrophy />;
        case "XP_BOOST":
            return <MdLeaderboard />;
        default:
            return <FaGift />;
    }
};

export const RewardsSection: React.FC<RewardsSectionProps> = ({
    brandName = "Monster",
    pointsName = "Beast Points",
    currentPoints: _currentPoints = 0,
    currentLevel = 1,
    nextLevelPoints: _nextLevelPoints = 250,
    rewards = DEFAULT_REWARDS,
    heroImageUrl,
    spreadImageUrl,
    spreadMode,
    howToPlaySteps,
    requirementUnit,
    onEnterCode,
    onViewMissions: _onViewMissions,
    onViewChallenges,
    onViewRetailers,
    themeColors,
}) => {
    const carouselRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const steps = howToPlaySteps && howToPlaySteps.length > 0 ? howToPlaySteps : DEFAULT_HOW_TO_PLAY;

    // Check scroll state
    const updateScrollState = () => {
        if (carouselRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
        }
    };

    const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const scrollCarousel = (direction: "left" | "right") => {
        if (carouselRef.current) {
            const scrollAmount = 320;
            const newPosition = carouselRef.current.scrollLeft + (direction === "left" ? -scrollAmount : scrollAmount);
            carouselRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
            clearTimeout(scrollTimerRef.current);
            scrollTimerRef.current = setTimeout(updateScrollState, 300);
        }
    };

    const isRewardUnlocked = (reward: RewardTier) => {
        return currentLevel >= reward.requiredLevel;
    };

    const accentColor = themeColors?.accent || themeColors?.primary;

    const sectionStyle: React.CSSProperties = {
        "--rewards-accent-color": accentColor,
    } as React.CSSProperties;

    // Step click handlers — map by index to existing callbacks
    const stepClickHandlers = [onViewRetailers, onViewChallenges, onEnterCode];

    // Determine requirement label for reward cards
    const getRequirementLabel = (reward: RewardTier) => {
        if (requirementUnit) {
            return `${reward.requiredLevel} ${requirementUnit}`;
        }
        // Points-based display when requiredPoints is set and no custom unit
        if (reward.requiredPoints) {
            return `${reward.requiredPoints.toLocaleString()} ${pointsName}`;
        }
        // Backward compat: Monster "cans" pattern
        return `${reward.requiredLevel} ${reward.requiredLevel === 1 ? "can" : "cans"}`;
    };

    /*
     * Monster-specific corner decorations only show when no custom hero or steps are configured.
     * When both are absent the caller is using the component in its original Monster Energy
     * backward-compat mode (hardcoded defaults). Any page-builder-driven hub supplies at least
     * one of these props, disabling the Monster chrome.
     */
    const isMonsterTheme = !heroImageUrl && !howToPlaySteps;

    return (
        <section className="rewards-section" style={sectionStyle}>
            {/* Section Header */}
            <h2 className="rewards-section__title">
                Earn {pointsName} to Unlock Bonus In-Game Rewards
            </h2>

            {/* How to Play Section */}
            <div className="rewards-section__how-to-play">
                {/* Left side — hero image */}
                <div className="rewards-section__how-to-play-image">
                    {heroImageUrl
                        ? <img
                            className="rewards-section__hero-image"
                            src={heroImageUrl}
                            alt={`${brandName} rewards`}
                        />
                        : spreadMode === "none"
                            ? null
                            : spreadMode === "custom" && spreadImageUrl
                                ? <img
                                    className="rewards-section__hero-image"
                                    src={spreadImageUrl}
                                    alt={`${brandName} products`}
                                />
                                : <div className="rewards-section__cans-spread">
                                    <img
                                        className="rewards-section__can-side rewards-section__can-side--left"
                                        src={`${MONSTER_CDN_BASE}/can-zero-sugar-back.webp`}
                                        alt={`${brandName} Zero Sugar with Call of Duty promo`}
                                    />
                                    <img
                                        className="rewards-section__can-center"
                                        src={`${MONSTER_CDN_BASE}/monster-energy-can.png`}
                                        alt={`${brandName} Energy`}
                                    />
                                    <img
                                        className="rewards-section__can-side rewards-section__can-side--right"
                                        src={`${MONSTER_CDN_BASE}/can-ultra-back.webp`}
                                        alt={`${brandName} Ultra with Call of Duty promo`}
                                    />
                                </div>
                    }
                </div>

                {/* Right side - Configurable Steps */}
                <div className="rewards-section__how-to-play-steps">
                    {steps.map((step, index) =>
                        <button
                            key={step.title}
                            className="rewards-section__step"
                            onClick={stepClickHandlers[index]}
                        >
                            <div className="rewards-section__step-icon" style={{ borderColor: accentColor, color: accentColor }}>
                                {getStepIcon(step.icon)}
                            </div>
                            <div className="rewards-section__step-content">
                                <span className="rewards-section__step-title">{step.title}</span>
                                <span className="rewards-section__step-desc">
                                    {step.description}
                                </span>
                            </div>
                        </button>)}
                </div>
            </div>

            {/* Unlockable Rewards Carousel */}
            <div className="rewards-section__rewards">
                <div className="rewards-section__rewards-header">
                    <h3 className="rewards-section__rewards-title">
                        Unlock Rewards
                    </h3>
                    <div className="rewards-section__carousel-nav">
                        <button
                            className="rewards-section__nav-btn"
                            onClick={() => scrollCarousel("left")}
                            disabled={!canScrollLeft}
                            aria-label="Scroll left"
                        >
                            <FaChevronLeft />
                        </button>
                        <button
                            className="rewards-section__nav-btn"
                            onClick={() => scrollCarousel("right")}
                            disabled={!canScrollRight}
                            aria-label="Scroll right"
                        >
                            <FaChevronRight />
                        </button>
                    </div>
                </div>

                <div
                    className="rewards-section__carousel"
                    ref={carouselRef}
                    onScroll={updateScrollState}
                >
                    {rewards.map((reward) => {
                        const unlocked = isRewardUnlocked(reward);
                        return (
                            <div
                                key={reward.id}
                                className={`rewards-section__reward-card ${unlocked ? "rewards-section__reward-card--unlocked" : ""}`}
                            >
                                {/* Lock/Unlock Badge Header */}
                                <div className="rewards-section__reward-badge">
                                    <span className="rewards-section__reward-badge-text">
                                        {unlocked ? "UNLOCKED" : "UNLOCK"}
                                    </span>
                                    <span className="rewards-section__reward-badge-icon">
                                        {unlocked ? <FaUnlock /> : <FaLock />}
                                    </span>
                                </div>

                                {/* Reward Image */}
                                <div className="rewards-section__reward-image">
                                    {reward.imageUrl
                                        ? <img src={reward.imageUrl} alt={reward.name} />
                                        : <div className="rewards-section__reward-placeholder">
                                            {getCategoryIcon(reward.category)}
                                        </div>
                                    }
                                </div>

                                {/* Reward Info */}
                                <div className="rewards-section__reward-info">
                                    <p className="rewards-section__reward-text">
                                        <span className="rewards-section__reward-name">
                                            {reward.name}
                                        </span>
                                        {reward.bonusText &&
                                            <span className="rewards-section__reward-bonus">
                                                {reward.bonusText}
                                            </span>
                                        }
                                    </p>
                                    <span className="rewards-section__reward-requirement">
                                        {getRequirementLabel(reward)}
                                    </span>
                                </div>

                                {/* Corner Decoration — only for Monster-style (backward compat) */}
                                {isMonsterTheme &&
                                    <img
                                        className="rewards-section__reward-corner"
                                        src={unlocked ? `${MONSTER_CDN_BASE}/corner-active1.svg` : `${MONSTER_CDN_BASE}/corner-codes.svg`}
                                        alt=""
                                        aria-hidden="true"
                                    />
                                }
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default RewardsSection;
